<template>
  <v-container>
    <PageHeader noPadding title="Monthly RISS Report" icon="poll" />
    <v-progress-linear indeterminate v-if="globalIsLoading"></v-progress-linear>
    <v-alert type="info" :value="(!globalIsLoading) && (reportVisibilityOption.length == 0)">
      There are no report types activated for this tenant.
      Please contact the system administrator to activate reports.
    </v-alert>

    <v-layout row wrap v-if="selectedReport === null && !globalIsLoading">
      <v-flex xs12 md12 v-if="reportTypeIsVisible(reportObjects.riss.configKey)">
        <v-card>
          <v-card-actions>
            <v-btn :loading="loading.report" color="info" @click="downloadRissReport" :disabled="!rissSelectedReportMonthYear.year">
              <v-icon class="mr-1">save_alt</v-icon>
              {{ $t('Button.Download') }}
            </v-btn>
          </v-card-actions>
          <v-divider />
          <v-card-title class="headline">
            Summary Report
          </v-card-title>
          <v-card-text class="pt-0">
            Summary report for surface water and process water from a location.
          </v-card-text>
          <v-card-text>
            <v-autocomplete style="max-width: 300px;" v-model="selectedLocation" label="Discrete Station" solo :items="discreteLocations" item-text="primaryName" return-object />

            <v-select style="max-width: 300px;" solo v-bind:items="rissSelectItems" v-model="rissSelectedReportMonthYear" :label="reportLabel" single-line menu-props="bottom" item-text="text" :disabled="!selectedStationName || !rissSelectItems.length" return-object :loading="this.loading.rissMonthYearList" />
          </v-card-text>

        </v-card>
      </v-flex>
      <v-snackbar v-model="snackbar" :top="true" :timeout="snackTimeout" :vertical="true" :color="snackColor">
        {{ snackMessage }}
        <v-btn dark flat @click="snackbar = false">{{ $t('Button.Close') }}</v-btn>
      </v-snackbar>
    </v-layout>
    <WaterQualityReportsErrorModal v-model="errorModal" :data="errorModalData" :loading="errorModalLoading" :customTitle="customTitle" @download="onErrorModalCallback" @cancel="onErrorModalClose" />
    <ErrorDialog v-model="errorDialog.show" :data="errorDialog.data" :title="errorDialog.title"></ErrorDialog>
  </v-container>
</template>

<script>
  import MomentUtil from '@/components/mixins/MomentUtil'
  import DownloadUtil from '@/components/mixins/DownloadUtil'
  import SnackBarUtil from '@/components/mixins/SnackBarUtil'
  import ErrorDialog from '@/components/generic/ErrorDialog'
  import ReportsErrorModalUtil from '@/components/mixins/ReportsErrorModalUtil'
  import stringUtils from '@/string-utils'
  import moment from 'moment'
  import axios from 'axios'
  import WaterQualityReportsErrorModal from '@/components/WaterQualityReportsErrorModal'

  import {
    SOURCE_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'

  import {
    createNamespacedHelpers,
  } from 'vuex'
  import {
    UiConfigKeys,
  } from '@/store/UI/uiconfig'
  import {
    reportTypes,
  } from '@/store/stations'

  const authenticate = createNamespacedHelpers('authenticate')
  const uiConfig = createNamespacedHelpers('uiconfig')
  const dataSources = createNamespacedHelpers('datasource')
  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const app = createNamespacedHelpers('app')
  const navigation = createNamespacedHelpers('navigation')

  export default {
    components: {
      WaterQualityReportsErrorModal,
      ErrorDialog,
    },
    mixins: [MomentUtil, DownloadUtil, SnackBarUtil, ReportsErrorModalUtil],
    data() {
      return {
        selectedReportMonthYear: {},
        rissSelectedReportMonthYear: {},
        items: [],
        rissItems: [],
        selectedReport: null,
        selectedLocation: null,
        hasUsedRissStationSelector: false,
        loading: {
          report: false,
          monthYearList: false,
          rissMonthYearList: false,
        },
      }
    },
    computed: {
      ...authenticate.mapGetters(['currentTenant']),
      ...uiConfig.mapGetters(['uiConfigOptions', 'uiConfigOptionByKey']),
      ...dataSources.mapGetters(['dataSources']),
      ...monitoringFeatures.mapGetters(['getDisplayNameById']),
      ...navigation.mapGetters(['searchableLocationItemsIndexByType']),
      discreteLocations() {
        return this.searchableLocationItemsIndexByType[SOURCE_TYPES.discreteEnvirodata]
      },
      selectedStation() {
        return this.selectedLocation && this.selectedLocation.dataTypesIndex[SOURCE_TYPES.discreteEnvirodata]
      },
      selectedStationName() {
        return this.selectedStation && this.selectedStation.LocationIdentifier
      },
      rissSelectItems() {
        if (this.rissItems == null) {
          return []
        }
        return this.rissItems.map(x => {
          return {
            text: x.format('MMM-YYYY'),
            month: x.month() + 1,
            year: x.year(),
          }
        })
      },
      globalIsLoading() {
        return this.uiConfigOptions.isLoading
      },
      reportObjects() {
        return reportTypes
      },
      reportVisibilityOption() {
        let data = this.uiConfigOptionByKey(
          UiConfigKeys.ReportComponentVisibility
        )
        if (!(data && data.Value)) {
          return []
        }
        return JSON.parse(data.Value)
      },
      reportLabel() {
        if (this.rissSelectItems.length > 0) return 'Select month'
        else if (this.hasUsedRissStationSelector) return 'No reports available'
      },
      errorDialog() {
        return this.$store.getters['app/errorDialog']
      },
    },
    watch: {
      selectedStationName(value) {
        if (!value) return
        this.hasUsedRissStationSelector = true
        this.rissItems = []
        this.rissSelectedReportMonthYear = {}
        axios.get(`/api/WaterQuality/AvailableMonths/Station/${stringUtils.fixedEncodeURIComponent(value)}`)
          .then(res => {
            this.rissItems = res.data.map(x => moment(x))
          })
      },
      loadingHasFinish: {
        immediate: true,
        handler(value) {
          if (value) this.fetchUiConfigOptions()
        },
      },
    },
    methods: {
      ...uiConfig.mapActions(['getUiConfigOptions']),
      ...app.mapActions(['setSnackbar', 'loadingHasFinish']),
      async downloadRissReport({
        suppressErrors,
      }) {
        let params = {
          suppressErrors: suppressErrors || false,
        }
        this.showDownloadMessage()
        this.loading.report = true
        var url =
          '/Report/RISS?downloadMonth=' +
          this.rissSelectedReportMonthYear.month +
          '&downloadYear=' +
          this.rissSelectedReportMonthYear.year +
          '&stationToFetch=' + stringUtils.fixedEncodeURIComponent(this.selectedStationName)
        var startDate = moment(this.rissSelectedReportMonthYear.text)
        var endDate = (moment(this.rissSelectedReportMonthYear.text)
          .add(1, 'months')
          .add(-1, 'days'))

        var fileName = (
          this.selectedStationName +
          '_RISS_Report_From_' +
          startDate.format('YYYY-MMM-DD') +
          '_To_' +
          endDate.format('YYYY-MMM-DD') +
          '.xlsx')

        let options = {
          params: params,
        }

        try {
          await this.download(url, fileName, options)
          this.resetErrorModal()
        } catch (error) {
          this.loading.report = false
          let { data, customTitle } = this.generateReportTitleAndData(error.response.data)
          this.setErrorModal(data, () => {
            this.downloadRissReport({
              suppressErrors: true,
            })
          }, customTitle)
        } finally {
          this.loading.report = false
        }
      },
      showDownloadMessage() {
        this.setSnackbar({
          text: 'The download may take some time to finish. Please do not close your browser until the download is complete.',
          color: 'info',
          icon: 'info',
          timeout: 8000,
        })
      },
      reportTypeIsVisible(reportTypeConfigKey) {
        return this.reportVisibilityOption.includes(reportTypeConfigKey)
      },
      fetchUiConfigOptions() {
        let options = {
          config: {},
          forced: false,
          successCb: () => {
            this.fetchAvailableMonths()
          },
          failedCb: (msg) => {
            console.error(msg)
            this.$store.dispatch('app/setErrorDialog', {
              data: `failed to fetch report visibility options: ${msg}`,
              title: msg.message,
              show: true,

            })
          },
        }

        this.getUiConfigOptions(options)
      },
      fetchAvailableMonths() {

        if (!this.currentTenant || !this.currentTenant.name || this.reportVisibilityOption == []) return
        if (this.reportVisibilityOption == []) {
          return
        }
        this.loading.monthYearList = true

        axios
          .get(
            '/api/WaterQuality/AvailableMonths/' + this.currentTenant.name)
          .then(res => {
            this.loading.monthYearList = false
            this.items = res.data.map(x => moment(x))
          })
          .catch(err => {
            this.isLoadingMonthYearList = false
            console.error(err)
            this.$store.dispatch('app/setErrorDialog', {
              data: `failed to fetch report availability ${err}`,
              title: err.message,
              show: true,
            })
          })
      },
    },
  }
</script>